import {
    useForm as useReactHookForm,
    useFormContext as useReactHookFormContext,
    UseFormReturn,
    UseFormProps,
    RegisterOptions,
    Path,
    UseFormRegisterReturn,
    FieldValues,
    FieldError
} from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { get } from 'lodash'
import { useTranslations } from 'next-intl'
import { InputProps } from '../v2/shared/components/Input'
import { useEffect } from 'react'

type TranslationFunction = ReturnType<typeof useTranslations>

type ExtendedRegisterReturn<
    TFieldValues extends FieldValues = FieldValues,
    TFieldName extends Path<TFieldValues> = Path<TFieldValues>
> = UseFormRegisterReturn<TFieldName> & {
    state: InputProps['state']
    message?: string
    required?: boolean
}

type CustomUseFormReturn<TFieldValues extends FieldValues = FieldValues> = Omit<
    UseFormReturn<TFieldValues>,
    'register'
> & {
    register: <TFieldName extends Path<TFieldValues>>(
        name: TFieldName,
        options?: RegisterOptions<TFieldValues, TFieldName>
    ) => ExtendedRegisterReturn<TFieldValues, TFieldName>
}

const getErrorMessage = (error: FieldError | undefined): string | undefined => {
    if (!error) return undefined
    return typeof error.message === 'string' ? error.message : undefined
}

const getBaseSchema = (schema: z.ZodTypeAny): z.ZodObject<any> | null => {
    if (schema instanceof z.ZodObject) {
        return schema
    }
    if (schema instanceof z.ZodEffects) {
        const innerSchema = getBaseSchema(schema.innerType())
        return innerSchema
    }
    return null
}

const getNestedSchema = (schema: z.ZodTypeAny, path: string): z.ZodTypeAny | null => {
    const baseSchema = getBaseSchema(schema)
    if (!baseSchema) return null

    const parts = path.split('.')
    let currentSchema: z.ZodTypeAny = baseSchema

    for (const part of parts) {
        if (currentSchema instanceof z.ZodObject) {
            currentSchema = currentSchema.shape[part]
        } else if (currentSchema instanceof z.ZodArray) {
            currentSchema = currentSchema.element
            continue
        } else if (currentSchema instanceof z.ZodEffects) {
            const innerSchema = getBaseSchema(currentSchema)
            if (!innerSchema) return null
            currentSchema = innerSchema.shape[part]
        } else {
            return null
        }

        if (!currentSchema) return null
    }

    return currentSchema
}

const isFieldRequired = (
    fieldSchema: z.ZodTypeAny | null,
    fieldError: FieldError | undefined,
    formState: any,
    fieldName: string
): boolean => {
    const isStaticallyRequired = fieldSchema ? !fieldSchema.isOptional?.() : false

    const hasRequiredError = fieldError?.message?.toLowerCase().includes('required')

    const fieldParts = fieldName.split('.')
    if (fieldParts.length > 1) {
        const groupName = fieldParts[0]
        const groupErrors = get(formState.errors, groupName)

        const isArrayField = fieldParts.some((part) => !isNaN(Number(part)))

        if (isArrayField && groupErrors && typeof groupErrors === 'object') {
            const arrayItemErrors = get(groupErrors, fieldParts.slice(1, -1).join('.'))
            if (arrayItemErrors && typeof arrayItemErrors === 'object') {
                const siblingHasRequiredError = Object.values(arrayItemErrors).some((error: any) =>
                    error?.message?.toLowerCase().includes('required')
                )
                if (siblingHasRequiredError) {
                    return true
                }
            }
        } else if (groupErrors && typeof groupErrors === 'object') {
            const siblingHasRequiredError = Object.values(groupErrors).some((error: any) =>
                error?.message?.toLowerCase().includes('required')
            )
            if (siblingHasRequiredError) {
                return true
            }
        }
    }

    return isStaticallyRequired || Boolean(hasRequiredError)
}

export function useForm<TSchema extends z.ZodType<any, any, any>>({
    schema,
    ...props
}: Omit<UseFormProps<z.infer<TSchema>>, 'resolver'> & {
    schema: TSchema
}): CustomUseFormReturn<z.infer<TSchema>> {
    const t = useTranslations('zod')
    const methods = useReactHookForm<z.infer<TSchema>>({
        ...props,
        resolver: zodResolver(schema)
    })

    useEffect(() => {
        if (schema) {
            const zodErrorMap = createCustomErrorMap(t)
            z.setErrorMap(zodErrorMap)
        }
    }, [t, schema])

    const enhancedRegister = <TFieldName extends Path<z.infer<TSchema>>>(
        name: TFieldName,
        options?: RegisterOptions<z.infer<TSchema>, TFieldName>
    ): ExtendedRegisterReturn<z.infer<TSchema>, TFieldName> => {
        const original = methods.register(name, options)
        const fieldSchema = getNestedSchema(schema, name as string)
        const fieldError = get(methods.formState.errors, name) as FieldError | undefined

        return {
            ...original,
            name,
            required: isFieldRequired(fieldSchema, fieldError, methods.formState, name as string),
            state: fieldError ? 'error' : 'default',
            message: getErrorMessage(fieldError)
        }
    }

    return {
        ...methods,
        register: enhancedRegister
    }
}

export function useFormContext<TSchema extends z.ZodType<any, any, any>>({
    schema
}: {
    schema: TSchema
}): CustomUseFormReturn<z.infer<TSchema>> {
    const methods = useReactHookFormContext<z.infer<TSchema>>()

    const enhancedRegister = <TFieldName extends Path<z.infer<TSchema>>>(
        name: TFieldName,
        options?: RegisterOptions<z.infer<TSchema>, TFieldName>
    ): ExtendedRegisterReturn<z.infer<TSchema>, TFieldName> => {
        const original = methods.register(name, options)
        const fieldSchema = getNestedSchema(schema, name as string)
        const fieldError = get(methods.formState.errors, name) as FieldError | undefined

        return {
            ...original,
            name,
            required: isFieldRequired(fieldSchema, fieldError, methods.formState, name as string),
            state: fieldError ? 'error' : 'default',
            message: getErrorMessage(fieldError)
        }
    }

    return {
        ...methods,
        register: enhancedRegister
    }
}

const hasInclusiveType = [
    'inclusive',
    'inclusive_one',
    'inclusive_other',
    'inclusive_with_path',
    'inclusive_with_path_one',
    'inclusive_with_path_other',
    'not_inclusive',
    'not_inclusive_one',
    'not_inclusive_other',
    'not_inclusive_with_path',
    'not_inclusive_with_path_one',
    'not_inclusive_with_path_other'
]

export const createCustomErrorMap = (t: TranslationFunction): z.ZodErrorMap => {
    return (issue, ctx) => {
        let key = ''
        const { code } = issue

        if (code === 'custom') {
            if (issue.params && 'code' in issue.params) {
                return {
                    message: t(issue.params.code, issue.params) ?? ctx.defaultError
                }
            }
        }

        if (code === 'too_small' && issue.type === 'string' && issue.minimum === 1) {
            return { message: t('invalid_type_received_undefined') }
        }

        if (code === 'invalid_type') {
            return { message: t('invalid_enum') }
        }

        const inclusiveType = Object.keys(issue).find((item) => hasInclusiveType.indexOf(item) !== -1)

        switch (code) {
            case 'too_big':
            case 'too_small':
                key = `${issue.code}.${issue.type}.${inclusiveType}`
                break
            case 'invalid_string':
                key = `${issue.code}.${issue.validation}`
                break
            default:
                key = issue.code
        }
        return { message: t(key as any, issue as any) ?? ctx.defaultError }
    }
}
